import { useState } from "react";
import { Link } from "react-router-dom";

import { useCandidatos, useEditarCuenta, useVista } from "../../api/hooks";
import { FASES, nombreEquipo, type CasoResumen, type CuentaDetalle } from "../../api/tipos";
import { useAvisos } from "../../shell/Aviso";
import { useFormularios } from "../../shell/Formularios";
import { Abierto, BarraFase, Clave, Fit, Num, Punto, fmtFecha } from "../../ui/primitivas";
import type { Pestana } from "./Cuenta";

export function Resumen({ c, caso, ir }: { c: CuentaDetalle; caso: CasoResumen | null; ir: (p: Pestana) => void }) {
  const { abrir } = useFormularios();
  const { avisar } = useAvisos();
  const { data: vista } = useVista(caso?.id ?? null);
  const { data: candidatos } = useCandidatos(caso && caso.estado !== "F2" ? caso.id : null);
  const editar = useEditarCuenta(c.id);
  const [editando, setEditando] = useState(false);
  const [proxima, setProxima] = useState(c.proxima_accion);
  const [compromiso, setCompromiso] = useState(c.compromiso_fecha ?? "");
  const f = FASES[c.fase];
  const bloqueos = vista?.siguientes_transiciones[0]?.bloqueos ?? [];
  const siguiente = vista?.siguientes_transiciones[0];

  const guardar = async () => {
    await editar.mutateAsync({ proxima_accion: proxima, compromiso_fecha: compromiso || null });
    setEditando(false);
    avisar("Próxima acción actualizada");
  };

  const camino: [string, boolean, Pestana | null][] = [
    ["Cuenta creada", true, null],
    ["Primer contacto", c.interacciones.length > 0, "interacciones"],
    ["Descubrimiento", !!caso && caso.estado !== "D0", "descubrimiento"],
    ["Cierre D5 · indicador", !!vista?.cierre.indicador, "descubrimiento"],
    ["Propuesta", c.propuestas.length > 0 || caso?.estado === "D6", "propuestas"],
    ["Proyecto y cobros", c.proyectos.length > 0, c.proyectos.length > 0 ? "proyecto" : null],
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px,1fr))", gap: 16, alignItems: "start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <section className="tarjeta">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 12 }}>
            <h2 className="seccion-h">Fase actual</h2>
            {caso && vista && <span className="mono" style={{ color: "var(--muted)" }}>{vista.grafo.verificados} verificados · {vista.grafo.zonas_activas} zonas</span>}
          </div>
          {caso ? (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
                <span className="mono" style={{ fontSize: 22, color: "var(--sky)", fontWeight: 500 }}>{caso.estado}</span>
                <span className="subtitulo" style={{ fontSize: 20 }}>{caso.estado === "F2" ? "Salida F2" : f.nombre}</span>
              </div>
              <BarraFase fase={c.fase} ancho="100%" mostrarId={false} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, marginTop: 6 }}>
                {FASES.map((p, i) => <span key={p.id} className="mono" style={{ fontSize: 11, color: i === c.fase ? "var(--sky)" : "var(--faint)", fontWeight: i === c.fase ? 500 : 400 }}>{p.id}</span>)}
              </div>
              {siguiente && bloqueos.length > 0 && (
                <Abierto style={{ marginTop: 14, padding: "10px 12px", fontSize: 13, borderRadius: "var(--r-control)" }}>
                  Faltan {bloqueos.length} condiciones para {siguiente.hacia}. <a href="#" onClick={(e) => { e.preventDefault(); ir("descubrimiento"); }} style={{ color: "var(--open-text)", textDecoration: "underline" }}>Ver huecos →</a>
                </Abierto>
              )}
              {siguiente?.permitida && (
                <div style={{ marginTop: 14, background: "var(--verified-bg)", color: "var(--verified)", borderRadius: "var(--r-control)", padding: "10px 12px", fontSize: 13 }}>
                  Listo para pasar a {siguiente.hacia}. <a href="#" onClick={(e) => { e.preventDefault(); ir("descubrimiento"); }} style={{ color: "var(--verified)", textDecoration: "underline" }}>Avanzar →</a>
                </div>
              )}
            </>
          ) : (
            <Abierto>
              El Recorrido no ha arrancado. Al iniciarlo, la cuenta pasa a <strong>En descubrimiento</strong> y se abre el expediente.
              <div style={{ marginTop: 10 }}><button className="btn btn-primario" onClick={() => abrir({ tipo: "descubrimiento", cuentaId: c.id, empresa: c.empresa })}>Iniciar descubrimiento →</button></div>
            </Abierto>
          )}
        </section>

        <section className="tarjeta">
          <h2 className="seccion-h" style={{ marginBottom: 10 }}>Brief</h2>
          {c.brief ? <p style={{ fontSize: 14, lineHeight: 1.55 }}>{c.brief}</p> : <p style={{ fontSize: 13, color: "var(--faint)" }}>Sin brief todavía.</p>}
          {c.notas && <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--muted)", marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>{c.notas}</p>}
        </section>

        <section className="tarjeta">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, gap: 8 }}>
            <h2 className="seccion-h">Próxima acción</h2>
            <span style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
              {c.compromiso_fecha && !editando && <span className="mono" style={{ color: "var(--proposed)" }}>vence {fmtFecha(c.compromiso_fecha)}</span>}
              {!editando && <button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5 }} onClick={() => setEditando(true)}>Editar</button>}
            </span>
          </div>
          {editando ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div className="campo"><input autoFocus value={proxima} onChange={(e) => setProxima(e.target.value)} onKeyDown={(e) => e.key === "Enter" && guardar()} /></div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div className="campo" style={{ flex: 1 }}><input type="date" value={compromiso} onChange={(e) => setCompromiso(e.target.value)} /></div>
                <button className="btn btn-secundario" onClick={() => setEditando(false)}>Cancelar</button>
                <button className="btn btn-primario" onClick={guardar} disabled={editar.isPending}>Guardar</button>
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 14, lineHeight: 1.5 }}>{c.proxima_accion || <span style={{ color: "var(--faint)" }}>Sin próxima acción.</span>}</p>
          )}
          {caso && candidatos && candidatos.length > 0 && (
            <Link to={`/verificacion/${caso.id}`} className="btn btn-primario" style={{ marginTop: 12, textDecoration: "none" }}>Verificar {candidatos.length} hallazgos →</Link>
          )}
        </section>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <section className="tarjeta" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 12px" }}>
          <Clave k="Contacto">{c.contacto}</Clave>
          <Clave k="Rol"><span style={{ color: c.rol === "Por confirmar" ? "var(--open-text)" : "var(--text)" }}>{c.rol}</span></Clave>
          <Clave k="Prioridad">{c.prioridad}</Clave>
          <Clave k="Fit"><Fit v={c.fit} /><span style={{ color: "var(--faint)", fontSize: 12 }}> / 100</span></Clave>
          <Clave k="Último contacto"><span className="mono" style={{ fontSize: 13, color: (c.dias_sin_contacto ?? 0) >= 12 ? "var(--risk)" : "var(--text)" }}>{fmtFecha(c.ultimo_contacto)}{c.dias_sin_contacto !== null ? ` · hace ${c.dias_sin_contacto} días` : ""}</span></Clave>
          <Clave k="Responsable">{nombreEquipo(c.responsable_id)}</Clave>
        </section>

        <section className="tarjeta">
          <h2 className="seccion-h" style={{ marginBottom: 10 }}>El camino</h2>
          {camino.map(([l, ok, t], i) => (
            <button key={l} onClick={() => t && ir(t)} style={{ display: "flex", gap: 10, alignItems: "center", width: "100%", padding: "5px 0", fontSize: 13.5, color: ok ? "var(--text)" : "var(--faint)", cursor: t ? "pointer" : "default" }}>
              <Num n={i + 1} hecho={ok} />{l}
            </button>
          ))}
        </section>

        <section className="tarjeta">
          <h2 className="seccion-h" style={{ marginBottom: 10 }}>Compromisos abiertos</h2>
          {c.compromiso_fecha ? (
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Punto estado="proposed" />
              <span style={{ fontSize: 13.5 }}>{c.proxima_accion}<span style={{ display: "block", color: "var(--muted)", fontSize: 12.5 }}>vence {fmtFecha(c.compromiso_fecha, true)}</span></span>
            </div>
          ) : <p style={{ color: "var(--faint)", fontSize: 13 }}>Ninguno.</p>}
        </section>

        {c.relacional && (
          <section className="tarjeta" style={{ background: "var(--sunken)", borderColor: "transparent" }}>
            <p style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5 }}>Origen relacional. Cuenta real, pero fuera de las métricas de conversión.</p>
          </section>
        )}
      </div>
    </div>
  );
}
